/**
 * PositionEditor - Handles inline editing and management of order positions
 * Supports editing quantities, part details, and type-specific data
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Tooltip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import { UnifiedOrder, OrderPosition, MaterialData, HeatTreatmentData, CoatingData, ToolData, CarbideData } from '../../types/unifiedOrderTypes';
import { unifiedOrderService } from '../../services/unifiedOrderService';
import { positionFileService } from '../../services/positionFileService';
import { useNotification } from '../../contexts/NotificationContext';
import { MaterialFields, HeatTreatmentFields, CoatingFields, ToolFields, CarbideFields } from '../forms/TypeSpecificFields';

interface PositionEditorProps {
  order: UnifiedOrder;
  onOrderUpdated: () => void;
}

interface EditingPosition extends OrderPosition {
  isEditing?: boolean;
}

export default function PositionEditor({ order, onOrderUpdated }: PositionEditorProps) {
  const [positions, setPositions] = useState<EditingPosition[]>(order.positions);
  const [editingPosition, setEditingPosition] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newPosition, setNewPosition] = useState<Partial<OrderPosition>>({
    position_number: (order.positions.length + 1),
    quantity: 1,
    part_name: '',
    part_number: '',
    unit_price: undefined,
    total_price: undefined,
    currency: 'EUR',
    notes: '',
    type_specific_data: getDefaultTypeSpecificData(order.order_type)
  });
  const [saving, setSaving] = useState(false);
  const [positionFiles, setPositionFiles] = useState<Map<string, any[]>>(new Map());
  const [fileUploadDialogOpen, setFileUploadDialogOpen] = useState(false);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const { showNotification } = useNotification();

  // Load position files when component mounts
  useEffect(() => {
    loadPositionFiles();
  }, [order.id]);

  const loadPositionFiles = async () => {
    try {
      const response = await positionFileService.getOrderPositionFiles(order.id);
      if (response.success && response.data) {
        const filesMap = new Map<string, any[]>();
        Object.entries(response.data).forEach(([positionId, files]) => {
          filesMap.set(positionId, files as any[]);
        });
        setPositionFiles(filesMap);
      }
    } catch (error: any) {
      console.error('Error loading position files:', error);
    }
  };

  function getDefaultTypeSpecificData(orderType: string): MaterialData | HeatTreatmentData | CoatingData | ToolData | CarbideData {
    switch (orderType) {
      case 'material':
        return {
          form: '',
          cross_section: '',
          length_mm: 0,
          steel_quality: '',
          internal_order_reference: ''
        } as MaterialData;
      case 'heat_treatment':
        return {
          treatment_type: '',
          hrc_range: '',
          steel_quality: '',
          weight_kg: 0,
          internal_order_reference: '',
          additional_info: ''
        } as HeatTreatmentData;
      case 'coating':
        return {
          treatment_type: '',
          coating_spec: '',
          steel_quality: '',
          weight_kg: 0,
          internal_order_reference: '',
          additional_info: ''
        } as CoatingData;
      case 'tool':
        return {
          manufacturer_article_number: '',
          tool_type: '',
          manufacturer: '',
          additional_info: ''
        } as ToolData;
      case 'carbide':
        return {
          material_spec: ''
        } as CarbideData;
      default:
        return {} as any;
    }
  }

  const startEditing = (positionId: string) => {
    setEditingPosition(positionId);
  };

  const cancelEditing = () => {
    setEditingPosition(null);
    // Reset positions to original state
    setPositions(order.positions);
  };

  const savePosition = async (position: OrderPosition) => {
    try {
      setSaving(true);
      const response = await unifiedOrderService.updateOrderPosition(order.id, position.id, {
        quantity: position.quantity,
        part_name: position.part_name,
        part_number: position.part_number,
        notes: position.notes,
        type_specific_data: position.type_specific_data,
        unit_price: position.unit_price,
        total_price: position.total_price,
        currency: position.currency
      });

      if (response.success) {
        showNotification('Position erfolgreich aktualisiert!', 'success'); /* Position updated successfully! */
        setEditingPosition(null);
        onOrderUpdated();
      } else {
        showNotification(response.error || 'Fehler beim Aktualisieren der Position', 'error'); /* Failed to update position */
      }
    } catch (error: any) {
      showNotification(`Fehler beim Aktualisieren der Position: ${error.message}`, 'error'); /* Error updating position: */
    } finally {
      setSaving(false);
    }
  };

  const addPosition = async () => {
    try {
      setSaving(true);
      const response = await unifiedOrderService.addOrderPosition(order.id, {
        position_number: newPosition.position_number!,
        quantity: newPosition.quantity!,
        part_name: newPosition.part_name || '',
        part_number: newPosition.part_number || '',
        notes: newPosition.notes || '',
        type_specific_data: newPosition.type_specific_data!
      });

      if (response.success) {
        showNotification('Position erfolgreich hinzugefügt!', 'success'); /* Position added successfully! */
        setAddDialogOpen(false);
        setNewPosition({
          position_number: (order.positions.length + 2),
          quantity: 1,
          part_name: '',
          part_number: '',
          unit_price: undefined,
          total_price: undefined,
          currency: 'EUR',
          notes: '',
          type_specific_data: getDefaultTypeSpecificData(order.order_type)
        });
        onOrderUpdated();
      } else {
        showNotification(response.error || 'Fehler beim Hinzufügen der Position', 'error'); /* Failed to add position */
      }
    } catch (error: any) {
      showNotification(`Fehler beim Hinzufügen der Position: ${error.message}`, 'error'); /* Error adding position: */
    } finally {
      setSaving(false);
    }
  };

  const deletePosition = async (positionId: string) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Position löschen möchten?')) { /* Are you sure you want to delete this position? */
      return;
    }

    try {
      setSaving(true);
      const response = await unifiedOrderService.deleteOrderPosition(order.id, positionId);

      if (response.success) {
        showNotification('Position erfolgreich gelöscht!', 'success'); /* Position deleted successfully! */
        onOrderUpdated();
      } else {
        showNotification(response.error || 'Fehler beim Löschen der Position', 'error'); /* Failed to delete position */
      }
    } catch (error: any) {
      showNotification(`Fehler beim Löschen der Position: ${error.message}`, 'error'); /* Error deleting position: */
    } finally {
      setSaving(false);
    }
  };

  const updatePositionField = (positionId: string, field: keyof OrderPosition, value: any) => {
    setPositions(prev => prev.map(pos => 
      pos.id === positionId 
        ? { ...pos, [field]: value }
        : pos
    ));
  };

  // File management functions
  const handleFileUpload = (positionId: string) => {
    setSelectedPositionId(positionId);
    setFileUploadDialogOpen(true);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !selectedPositionId) return;

    try {
      setUploadingFiles(true);
      const filesArray = Array.from(files);
      
      const response = await unifiedOrderService.uploadPositionFiles(order.id, selectedPositionId, filesArray);
      
      if (response.success) {
        showNotification(`${files.length} Datei(en) erfolgreich hochgeladen!`, 'success');
        await loadPositionFiles(); // Reload files
        setFileUploadDialogOpen(false);
        setSelectedPositionId(null);
      } else {
        showNotification('Fehler beim Hochladen der Dateien', 'error');
      }
    } catch (error: any) {
      showNotification(`Fehler beim Hochladen: ${error.message}`, 'error');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleFileDownload = (file: any) => {
    // Use file_path instead of filename to get the full path including storage directory
    const url = unifiedOrderService.getFileUrl(file.file_path || file.filename);
    window.open(url, '_blank');
  };

  const handleFileDelete = async (positionId: string, fileId: string) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Datei löschen möchten?')) {
      return;
    }

    try {
      const response = await positionFileService.deletePositionFile(order.id, positionId, fileId);
      if (response.success) {
        showNotification('Datei erfolgreich gelöscht!', 'success');
        await loadPositionFiles(); // Reload files
      } else {
        showNotification('Fehler beim Löschen der Datei', 'error');
      }
    } catch (error: any) {
      showNotification(`Fehler beim Löschen: ${error.message}`, 'error');
    }
  };

  const getPositionFiles = (positionId: string) => {
    return positionFiles.get(positionId) || [];
  };

  const renderTypeSpecificData = (data: any, orderType: string) => {
    if (!data || Object.keys(data).length === 0) {
      return <Typography variant="body2" color="text.secondary">Keine spezifischen Daten</Typography>; /* No specific data */
    }

    // Use the proper TypeSpecificFields components for rendering (read-only)
    switch (orderType) {
      case 'material':
        return <MaterialFields data={data as MaterialData} onChange={() => {}} disabled={true} />;
      case 'heat_treatment':
        return <HeatTreatmentFields data={data as HeatTreatmentData} onChange={() => {}} disabled={true} />;
      case 'coating':
        return <CoatingFields data={data as CoatingData} onChange={() => {}} disabled={true} />;
      case 'tool':
        return <ToolFields data={data as ToolData} onChange={() => {}} disabled={true} />;
      case 'carbide':
        return <CarbideFields data={data as CarbideData} onChange={() => {}} disabled={true} />;
      default:
        // Fallback to generic rendering for unknown types
        const fields = Object.entries(data).filter(([key, value]) => value !== '' && value !== null);
        return (
          <Grid container spacing={1}>
            {fields.map(([key, value]) => (
              <Grid item xs={6} key={key}>
                <Typography variant="caption" color="text.secondary">
                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
                <Typography variant="body2">
                  {String(value)}
                </Typography>
              </Grid>
            ))}
          </Grid>
        );
    }
  };

  const renderEditableTypeSpecificData = (position: OrderPosition) => {
    const data = position.type_specific_data;
    
    // Handle changes to type-specific data
    const handleTypeSpecificChange = (newData: any) => {
      updatePositionField(position.id, 'type_specific_data', newData);
    };

    // Use the proper TypeSpecificFields components for editing
    switch (order.order_type) {
      case 'material':
        return <MaterialFields data={data as MaterialData} onChange={handleTypeSpecificChange} disabled={false} />;
      case 'heat_treatment':
        return <HeatTreatmentFields data={data as HeatTreatmentData} onChange={handleTypeSpecificChange} disabled={false} />;
      case 'coating':
        return <CoatingFields data={data as CoatingData} onChange={handleTypeSpecificChange} disabled={false} />;
      case 'tool':
        return <ToolFields data={data as ToolData} onChange={handleTypeSpecificChange} disabled={false} />;
      case 'carbide':
        return <CarbideFields data={data as CarbideData} onChange={handleTypeSpecificChange} disabled={false} />;
      default:
        // Fallback to generic editing for unknown types
        return (
          <Grid container spacing={2}>
            {Object.entries(data).map(([key, value]) => (
              <Grid item xs={6} key={key}>
                <TextField
                  label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  value={String(value || '')}
                  onChange={(e) => {
                    const newData = { ...position.type_specific_data, [key]: e.target.value };
                    updatePositionField(position.id, 'type_specific_data', newData);
                  }}
                  size="small"
                  fullWidth
                />
              </Grid>
            ))}
          </Grid>
        );
    }
  };

  const getDeliveryStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'partial': return 'info';
      case 'delivered': return 'success';
      default: return 'default';
    }
  };

  // Determine if editing is allowed based on order status
  // Business Rule: Position editing only allowed for 'draft' status
  // Once PDF is generated (status becomes 'ready'), no more editing allowed
  const canEdit = order.status === 'draft';
  
  const getEditingRestrictionMessage = () => {
    if (order.status === 'ready') {
      return 'Positionsbearbeitung ist deaktiviert. PDF wurde für diese Bestellung generiert.'; /* Position editing is disabled. PDF has been generated for this order. */
    } else if (['sent', 'partial', 'delivered', 'completed'].includes(order.status)) {
      return `Positionsbearbeitung ist deaktiviert. Bestellstatus: ${order.status}`; /* Position editing is disabled. Order status: */
    }
    return '';
  };

  return (
    <Box>
      {/* Position Management Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Bestellpositionen ({positions.length}) {/* Order Positions */}
            </Typography>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
              >
                Position hinzufügen {/* Add Position */}
              </Button>
            )}
          </Box>
          
          {!canEdit && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="medium">
                Positionsbearbeitung eingeschränkt {/* Position Editing Restricted */}
              </Typography>
              <Typography variant="body2">
                {getEditingRestrictionMessage()}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Positions List */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Position</TableCell>
                  <TableCell>Teildetails</TableCell> {/* Part Details */}
                  <TableCell align="right">Menge</TableCell> {/* Quantity */}
                  <TableCell align="right">Geliefert</TableCell> {/* Delivered */}
                  <TableCell align="right">Stückpreis</TableCell> {/* Unit Price */}
                  <TableCell align="right">Gesamtpreis</TableCell> {/* Total Price */}
                  <TableCell>Status</TableCell>
                  <TableCell>Anmerkungen</TableCell> {/* Notes */}
                  {canEdit && <TableCell align="center">Aktionen</TableCell>} {/* Actions */}
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((position) => (
                  <React.Fragment key={position.id}>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          #{position.position_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {editingPosition === position.id ? (
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <TextField
                                label="Teilbezeichnung" /* Part Name */
                                value={position.part_name || ''}
                                onChange={(e) => updatePositionField(position.id, 'part_name', e.target.value)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label="Teilnummer" /* Part Number */
                                value={position.part_number || ''}
                                onChange={(e) => updatePositionField(position.id, 'part_number', e.target.value)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        ) : (
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {position.part_name || 'Kein Name'} {/* No name */}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {position.part_number || 'Keine Teilnummer'} {/* No part number */}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editingPosition === position.id ? (
                          <TextField
                            type="number"
                            value={position.quantity}
                            onChange={(e) => updatePositionField(position.id, 'quantity', parseInt(e.target.value) || 0)}
                            size="small"
                            inputProps={{ min: 1 }}
                            sx={{ width: 80 }}
                          />
                        ) : (
                          <Typography variant="body2" fontWeight="medium">
                            {position.quantity}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main">
                          {position.delivered_quantity || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {editingPosition === position.id ? (
                          <TextField
                            type="number"
                            value={position.unit_price || ''}
                            onChange={(e) => {
                              const unitPrice = parseFloat(e.target.value) || undefined;
                              updatePositionField(position.id, 'unit_price', unitPrice);
                              // Auto-calculate total price if quantity exists
                              if (unitPrice && position.quantity) {
                                updatePositionField(position.id, 'total_price', unitPrice * position.quantity);
                              }
                            }}
                            size="small"
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 100 }}
                            placeholder="€ 0.00"
                          />
                        ) : (
                          <Typography variant="body2" color="text.primary">
                            {(() => {
                              const price = position.unit_price;
                              if (price === null || price === undefined) return '-';
                              const numPrice = typeof price === 'number' ? price : parseFloat(String(price));
                              return isNaN(numPrice) ? '-' : `€ ${numPrice.toFixed(2)}`;
                            })()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editingPosition === position.id ? (
                          <TextField
                            type="number"
                            value={position.total_price || ''}
                            onChange={(e) => {
                              const totalPrice = parseFloat(e.target.value) || undefined;
                              updatePositionField(position.id, 'total_price', totalPrice);
                              // Auto-calculate unit price if quantity exists
                              if (totalPrice && position.quantity) {
                                updatePositionField(position.id, 'unit_price', totalPrice / position.quantity);
                              }
                            }}
                            size="small"
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 100 }}
                            placeholder="€ 0.00"
                          />
                        ) : (
                          <Typography variant="body2" color="text.primary" fontWeight="medium">
                            {(() => {
                              const price = position.total_price;
                              if (price === null || price === undefined) return '-';
                              const numPrice = typeof price === 'number' ? price : parseFloat(String(price));
                              return isNaN(numPrice) ? '-' : `€ ${numPrice.toFixed(2)}`;
                            })()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={position.delivery_status || 'pending'}
                          color={getDeliveryStatusColor(position.delivery_status || 'pending') as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {editingPosition === position.id ? (
                          <TextField
                            multiline
                            rows={2}
                            value={position.notes || ''}
                            onChange={(e) => updatePositionField(position.id, 'notes', e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="Positionsanmerkungen..." /* Position notes... */
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {position.notes || 'Keine Anmerkungen'} {/* No notes */}
                          </Typography>
                        )}
                      </TableCell>
                      {canEdit && (
                        <TableCell align="center">
                          {editingPosition === position.id ? (
                            <Box>
                              <Tooltip title="Änderungen speichern" /* Save Changes */>
                                <IconButton
                                  size="small"
                                  onClick={() => savePosition(position)}
                                  color="primary"
                                  disabled={saving}
                                >
                                  <SaveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Abbrechen" /* Cancel */>
                                <IconButton
                                  size="small"
                                  onClick={cancelEditing}
                                  disabled={saving}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box>
                              <Tooltip title="Position bearbeiten" /* Edit Position */>
                                <IconButton
                                  size="small"
                                  onClick={() => startEditing(position.id)}
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Position löschen" /* Delete Position */>
                                <IconButton
                                  size="small"
                                  onClick={() => deletePosition(position.id)}
                                  color="error"
                                  disabled={position.delivered_quantity > 0}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                    
                    {/* Type-specific data accordion */}
                    <TableRow>
                      <TableCell colSpan={canEdit ? 7 : 6} sx={{ p: 0, border: 'none' }}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2" color="text.secondary">
                              <InfoIcon sx={{ mr: 1, fontSize: 16, verticalAlign: 'middle' }} />
                              {order.order_type.replace('_', ' ').toUpperCase()} Spezifikationen {/* Specifications */}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {editingPosition === position.id ? 
                              renderEditableTypeSpecificData(position) : 
                              renderTypeSpecificData(position.type_specific_data, order.order_type)
                            }
                          </AccordionDetails>
                        </Accordion>
                      </TableCell>
                    </TableRow>

                    {/* Position files */}
                    <TableRow>
                      <TableCell colSpan={canEdit ? 7 : 6} sx={{ p: 0, border: 'none' }}>
                        <Card variant="outlined" sx={{ m: 1 }}>
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              Zugehörige Dateien {/* Related Files */}
                            </Typography>
                            
                            {/* File upload section */}
                            {canEdit && (
                              <Box sx={{ mb: 2 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<UploadIcon />}
                                  onClick={() => handleFileUpload(position.id)}
                                >
                                  Datei hochladen {/* Upload File */}
                                </Button>
                              </Box>
                            )}
                            
                            {/* File list */}
                            <Grid container spacing={1}>
                              {getPositionFiles(position.id).map((file) => (
                                <Grid item xs={12} sm={6} md={4} key={file.id}>
                                  <Card variant="outlined" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium">
                                        {file.originalname}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {file.size} Bytes
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleFileDownload(file)}
                                      >
                                        <DownloadIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleFileDelete(position.id, file.id)}
                                        color="error"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Position Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Neue Position hinzufügen</DialogTitle> {/* Add New Position */}
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Positionsnummer" /* Position Number */
                type="number"
                value={newPosition.position_number}
                onChange={(e) => setNewPosition(prev => ({
                  ...prev,
                  position_number: parseInt(e.target.value) || 1
                }))}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Menge" /* Quantity */
                type="number"
                value={newPosition.quantity}
                onChange={(e) => setNewPosition(prev => ({
                  ...prev,
                  quantity: parseInt(e.target.value) || 1
                }))}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Teilbezeichnung" /* Part Name */
                value={newPosition.part_name}
                onChange={(e) => setNewPosition(prev => ({
                  ...prev,
                  part_name: e.target.value
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Teilnummer" /* Part Number */
                value={newPosition.part_number}
                onChange={(e) => setNewPosition(prev => ({
                  ...prev,
                  part_number: e.target.value
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Anmerkungen" /* Notes */
                multiline
                rows={2}
                value={newPosition.notes}
                onChange={(e) => setNewPosition(prev => ({
                  ...prev,
                  notes: e.target.value
                }))}
                fullWidth
                placeholder="Optionale Positionsanmerkungen..." /* Optional position notes... */
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                {order.order_type.replace('_', ' ').toUpperCase()} Spezifikationen {/* Specifications */}
              </Typography>
              
              {/* Type-specific fields */}
              {newPosition.type_specific_data && Object.entries(newPosition.type_specific_data).map(([key, value]) => (
                <Grid item xs={6} key={key} sx={{ mt: 1 }}>
                  <TextField
                    label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    value={String(value || '')}
                    onChange={(e) => {
                      const newData = { ...newPosition.type_specific_data, [key]: e.target.value };
                      setNewPosition(prev => ({ ...prev, type_specific_data: newData as any }));
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={saving}>
            Abbrechen {/* Cancel */}
          </Button>
          <Button
            onClick={addPosition}
            variant="contained"
            disabled={saving || !newPosition.quantity}
          >
            {saving ? 'Wird hinzugefügt...' : 'Position hinzufügen'} {/* Adding... : Add Position */}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Upload Dialog */}
      <Dialog
        open={fileUploadDialogOpen}
        onClose={() => setFileUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Datei(en) für Position hochladen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Unterstützte Dateiformate: PDF, DWG, DXF, JPG, PNG, und weitere (max. 10MB pro Datei)
          </Typography>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<UploadIcon />}
            sx={{ mt: 2 }}
          >
            Datei(en) auswählen
            <input
              type="file"
              hidden
              accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.step,.stp,.iges,.igs"
              multiple
              onChange={handleFileChange}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFileUploadDialogOpen(false)} disabled={uploadingFiles}>
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
